import type { RaRecord } from "react-admin";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date)
  );
}

function sanitizeString(value: string): string {
  return value.replace(/\r\n|\n|\r/g, " ").trim();
}

function serializeLeaf(value: unknown): unknown {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "string") {
    return sanitizeString(value);
  }
  return value;
}

/**
 * Flattens nested API records into dot-separated keys suitable for jsonexport.
 * Object arrays use the first element only (matches prior fmbData export shape).
 */
export function flattenRecordForCsvExport(record: RaRecord, prefix = ""): Record<string, unknown> {
  const flat: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(record)) {
    if (value === null || value === undefined) {
      continue;
    }

    const path = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue;
      }
      if (isPlainObject(value[0])) {
        Object.assign(flat, flattenRecordForCsvExport(value[0] as RaRecord, path));
      } else {
        flat[path] = value.map((item) => serializeLeaf(item)).join("; ");
      }
      continue;
    }

    if (isPlainObject(value)) {
      Object.assign(flat, flattenRecordForCsvExport(value as RaRecord, path));
      continue;
    }

    flat[path] = serializeLeaf(value);
  }

  return flat;
}

/** Ensures every export row shares the same column set with stable ordering. */
export function recordsForCsvExport(records: RaRecord[]): Record<string, unknown>[] {
  const flattened = records.map((record) => flattenRecordForCsvExport(record));
  const keys = [...new Set(flattened.flatMap((row) => Object.keys(row)))].sort();

  return flattened.map((row) => {
    const normalized: Record<string, unknown> = {};
    for (const key of keys) {
      const value = row[key];
      normalized[key] = value === undefined || value === null ? "" : value;
    }
    return normalized;
  });
}
